
import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { merge } from 'lodash-es';
import * as _ from 'lodash';
import { MergeCustomerService } from '../../merge-customer.service';
import { TablesTranslations } from '~shared/components/tables/tables-translations.service';
import { Club, Country, Customer, CustomerTranslations, MergeAddress } from '~core/models';
import { NavigationService } from '~core/services';
import { CustomerAddressTranslationsService, CustomerStatusTranslationsService, MembershipStatusTranslationsService, SalutationsTranslationService } from '~shared/translations';

/**
 * Displays the customer merge process page.
 */
@UntilDestroy()
@Component({
  selector: 'app-assemble-data',
  templateUrl: 'assemble-data.component.html',
  styleUrls: ['../../merge-customer.component.scss']
})
export class AssembleDataComponent implements OnInit, OnDestroy {
  @Input() public selectedClub: Club;
  @Input() public countries: Array<Country> = [];
  @Input() public firstCustomerData: any = [];
  @Input() public secondCustomerData: any = [];

  public combinedCustomer: Customer;
  public translations: CustomerTranslations;

  constructor(
    public navigationService: NavigationService,

    private addressTranslations: CustomerAddressTranslationsService,
    private statusTranslations: CustomerStatusTranslationsService,
    private membershipTranslations: MembershipStatusTranslationsService,
    private salutationsTranslations: SalutationsTranslationService,
    public tablesTranslations: TablesTranslations,
    private customerMergeService: MergeCustomerService,
  ) {
    this.translations = {
      addressTranslations: this.addressTranslations,
      statusTranslations: this.statusTranslations,
      membershipTranslations: this.membershipTranslations,
      salutationsTranslations: this.salutationsTranslations
    };
    this.customerMergeService.castCombinedCustomer.pipe(untilDestroyed(this))
      .subscribe(data => this.combinedCustomer = data);
  }

  public ngOnInit(): void {
    const newCombinedCustomer = new Customer(null, this.translations);
    newCombinedCustomer['attributes'] = [];
    this.customerMergeService.setCombinedCustomer(newCombinedCustomer);

    this.initialSetup();
  }

  public ngOnDestroy(): void { }

  get triggered(): boolean {
    return this.customerMergeService.triggered;
  }

  initialSetup() {
    /**
     * We add keys to this array, that will check if identical value found on both customers
     */
    const equalValuesArray = [
      ['title', 'salutation', 'firstName', 'lastName'],
      ['company'],
      ['birthday'],
      ['phone'],
      ['mobile'],
      ['addresses'],
    ];

    /**
     * We add keys to this array, that will check if one customer has this data while the other does not
     */
    const singleValuesArray = [
      ['title', 'salutation', 'firstName', 'lastName'],
      ['birthday'],
      ['phone'],
      ['mobile'],
      ['searchEmail'],
      ['membership'],
      ['campaiMembership'],
      ['addresses'],
    ];

    /**
     * If values of these keys are equal on both customers, then we just pre-select them since they are required
     */
    for (const key of equalValuesArray) {
      if (this.checkIfEqualCustomerValue(key)) {
        this.addValueToCombinedCustomer(key);
      }
    }

    /**
     * If values of these keys exists on one customer only, then we just pre-select that value since it is required
     */
    for (const key of singleValuesArray) {
      const customerValue = this.checkIfSingleCustomerValue(key);
      if (customerValue === 1) {
        this.addValueToCombinedCustomer(key, 1);
      } else if (customerValue === 2) {
        this.addValueToCombinedCustomer(key, 2);
      }
    }


    this.fillAttributes();
  }

  /**
   * Updates the combined customer
   */
  setCombinedCustomer(customer: any): void {
    const mergedCustomer = merge(this.combinedCustomer, customer);
    this.customerMergeService.setCombinedCustomer(mergedCustomer);
  }

  /**
   * Clears and updates the combined customer
   */
  clearAndSet(customer: any): void {
    this.combinedCustomer['attributes'] = [];
    this.combinedCustomer['addresses'] = [];
    this.setCombinedCustomer(customer);
    this.initialSetup();
  }

  /**
   * Checks if values of the keys are the same on both customers
   *
   * @param keys an array of strings that are checked
   * @returns boolean based on if every key is equal among the customers
   */
  public checkIfEqualCustomerValue(keys: Array<string>): boolean {
    const arr = [];
    for (const key of keys) {
      const { firstCustomerValue, secondCustomerValue } = this.getCustomerValuesFromPath(key);
      if (key === 'addresses' && !this.combinedCustomer['addresses'].length) {
        const firstAddress = new MergeAddress(firstCustomerValue.find(el => el.type === 'standard'));
        const secondAddress = new MergeAddress(secondCustomerValue.find(el => el.type === 'standard'));
        arr.push(_.isEqual(firstAddress, secondAddress));
      } else {
        arr.push(_.isEqual(firstCustomerValue, secondCustomerValue));
      }
    }
    return arr.every(el => el === true);
  }

  /**
   * Checks if values of the keys exists on only a single customer
   *
   * @param keys an array of strings that are checked
   * @returns number based on which customer has data that the other does not
   */
  // eslint-disable-next-line complexity
  public checkIfSingleCustomerValue(keys: Array<string>): 2 | 1 | 0 {
    const arr = [];
    for (const key of keys) {
      let { firstCustomerValue, secondCustomerValue } = this.getCustomerValuesFromPath(key);

      if (key === 'addresses') {
        const firstAddress = firstCustomerValue.find(el => el.type === 'standard');
        const secondAddress = secondCustomerValue.find(el => el.type === 'standard');
        firstCustomerValue = firstAddress ? new MergeAddress(firstAddress) : null;
        secondCustomerValue = secondAddress ? new MergeAddress(secondAddress) : null;
      }
      if (_.isEmpty(firstCustomerValue) && !_.isEmpty(secondCustomerValue)) {
        arr.push(2);
      } else if (_.isEmpty(secondCustomerValue) && !_.isEmpty(firstCustomerValue)) {
        arr.push(1);
      } else {
        arr.push(0);
      }

    }

    if (arr.every(el => el === 2)) {
      return 2;
    } else if (arr.every(el => el === 1)) {
      return 1;
    } else {
      return 0;
    }
  }

  /**
   * Adds the values to the combined customer
   *
   * @param keys an array of strings that are checked
   * @param whichCustomer customer that will be the source customer, 1 is default
   */
  public addValueToCombinedCustomer(keys: Array<string>, whichCustomer: number = 1): void {
    const sourceCustomer = whichCustomer === 1 ? this.firstCustomerData[0] : this.secondCustomerData[0];
    const combinedCustomer = this.combinedCustomer;

    for (const key of keys) {
      if (key === 'addresses') {
        combinedCustomer[key].push(this.firstCustomerData[0][key].find(el => el.type === 'standard'));
      } else if (key === 'searchEmail') {
        combinedCustomer[key] = sourceCustomer[key];
        combinedCustomer['equalAuthorizationAndEmail'] = sourceCustomer['equalAuthorizationAndEmail'];
        combinedCustomer['externalAuthorization'] = sourceCustomer['externalAuthorization'];
      } else {
        combinedCustomer[key] = sourceCustomer[key];
      }
      this.customerMergeService.setCombinedCustomer(combinedCustomer);
    }
  }

  /**
   * Based on the path it return both customer values
   *
   * @param path key of the object
   * @returns both customer values
   */
  public getCustomerValuesFromPath(path: string) {
    const firstCustomerValue = _.get(this.firstCustomerData[0], path, '');
    const secondCustomerValue = _.get(this.secondCustomerData[0], path, '');
    return { firstCustomerValue, secondCustomerValue };
  }

  public fillAttributes(): void {
    this.combinedCustomer['attributes'] = [];
    const fCAttributes = this.firstCustomerData[0]['attributes'];
    const sCAttributes = this.secondCustomerData[0]['attributes'];
    const combinedCustomer = this.combinedCustomer;

    for (const attribute of fCAttributes) {
      combinedCustomer['attributes'].push(attribute);
    }
    for (const attribute of sCAttributes) {
      if (!combinedCustomer['attributes'].some(el => el.id === attribute.id)) {
        combinedCustomer['attributes'].push(attribute);
      }
    }

    this.customerMergeService.setCombinedCustomer(combinedCustomer);
  }
}
